import { useCallback, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent, Badge } from '@/components/v2/ui';
import { type ImageOptimizeState } from './useImageOptimizeState';
import { AnalysisGroup } from './AnalysisGroup';
import { RetouchGroup } from './RetouchGroup';
import { AspectRatioGroup } from './AspectRatioGroup';
import { FormatConvertSection } from './FormatConvertSection';
import { QualityGroup } from './QualityGroup';

interface Props {
  state: ImageOptimizeState;
  setState: Dispatch<SetStateAction<ImageOptimizeState>>;
}

export function ImageOptimizeControls({ state, setState }: Props) {
  const { t } = useTranslation();

  const handleToggle = useCallback((value: string) => {
    setState(prev => ({
      ...prev,
      openGroup: prev.openGroup === value ? null : value,
    }));
  }, [setState]);

  return (
    <div className="overflow-y-auto h-full border-l border-neutral-200 bg-white p-4 space-y-2">
      <Accordion type="single" collapsible value={state.openGroup ?? ''} onValueChange={handleToggle}>
        <AccordionItem value="analyse" className="border-l-2 border-l-primary-500 pl-2">
          <AccordionTrigger>
            <span className="flex items-center gap-2">
              {t('imageOptimize.groups.analyse')}
              <Badge variant="success" size="sm">{t('imageOptimize.analysis.free')}</Badge>
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <AnalysisGroup state={state} setState={setState} />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="retouch" className="border-l-2 border-l-orange-500 pl-2">
          <AccordionTrigger>{t('imageOptimize.groups.retusche')}</AccordionTrigger>
          <AccordionContent>
            <RetouchGroup state={state} setState={setState} />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="layout" className="border-l-2 border-l-primary-500 pl-2">
          <AccordionTrigger>{t('imageOptimize.groups.layoutRatio')}</AccordionTrigger>
          <AccordionContent>
            <AspectRatioGroup state={state} setState={setState} />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="format" className="border-l-2 border-l-primary-500 pl-2">
          <AccordionTrigger>{t('imageOptimize.groups.formatConvert')}</AccordionTrigger>
          <AccordionContent>
            <FormatConvertSection state={state} setState={setState} />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="quality" className="border-l-2 border-l-green-500 pl-2">
          <AccordionTrigger>
            <span className="flex items-center gap-2">
              {t('imageOptimize.groups.qualitaet')}
              <Badge variant="success" size="sm">{t('imageOptimize.analysis.free')}</Badge>
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <QualityGroup state={state} setState={setState} />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
